import { autoUpdate, flip, offset, useDismiss, useFloating, useInteractions } from '@floating-ui/react';
import { addYears, endOfDay, isValid, startOfDay } from 'date-fns';
import get from 'lodash/get';
import React, { useCallback, useEffect, useState } from 'react';
import { Calendar } from 'react-date-range';
import { useFormContext, useWatch } from 'react-hook-form';
import styled from 'styled-components';

import { themeDefault } from 'styles';
import { formatDate, parseLocalDate, todayDate } from 'utils/date-formatter';

import { Button } from '../../button';
import { IconCalendar, IconLockOutline } from '../../icon';
import { HorizontalLayout, VerticalLayout } from '../../Layout';
import { Title } from '../../Text';
import { useFieldValidation } from '../Form';
import { FormElement, FormLabel, InputDescription } from './FormCommons';
import { Input } from './Input';

const MIN_DATE = new Date(1800, 0, 1);
const MAX_DATE = addYears(todayDate(), 50);

interface DateInputProps {
    name: string;
    label: string;
    placeholder?: string;
    description?: string;
    disabled?: boolean;
    minDate?: Date;
    maxDate?: Date;
    disabledDates?: Date[];
    preventFlip?: boolean;
    $isVertical?: boolean;
    $outline?: boolean;
    $iconColor?: string;
}

export const FormDateInput = ({
    name,
    label,
    placeholder = 'MM-DD-YY',
    description,
    disabled,
    minDate,
    maxDate,
    disabledDates,
    preventFlip = false,
    $isVertical = false,
    $outline = false,
    $iconColor,
}: DateInputProps) => {
    const {
        formState: { errors },
        register,
        setValue,
        setError,
        clearErrors,
    } = useFormContext();
    register(name);
    const date = useWatch({ name });
    const error = get(errors, name);

    const { isRequired } = useFieldValidation(name);
    const [inputValue, setInputValue] = useState<string>('');

    useEffect(() => {
        setInputValue(formatDate(date));
    }, [date]);

    const onBlur = useCallback(() => {
        clearErrors(name);

        if (inputValue === '') {
            return setValue(name, '', {
                shouldDirty: true,
                shouldValidate: true,
            });
        }

        const newDate = new Date(inputValue);
        if (!isValid(newDate)) {
            return setError(name, { message: 'Invalid date' });
        }

        if (minDate && newDate < startOfDay(minDate)) {
            return setError(name, { message: `Date cannot be before ${formatDate(minDate)}` });
        }
        if (maxDate && newDate > endOfDay(maxDate)) {
            return setError(name, { message: `Date cannot be after ${formatDate(maxDate)}` });
        }

        setValue(name, inputValue ? formatDate(new Date(inputValue), 'SERVER') : '', {
            shouldDirty: true,
            shouldValidate: true,
        });
    }, [clearErrors, inputValue, maxDate, minDate, name, setError, setValue]);

    return (
        <Wrapper $outline={$outline}>
            <FormElement $isVertical={$isVertical}>
                {label ? (
                    <FormLabel htmlFor={name}>
                        {label}
                        {isRequired && '*'}
                    </FormLabel>
                ) : null}
                <HorizontalLayout $rowCenter $stretch>
                    <InputWrapper $stretch>
                        <IconContainer>{disabled && <IconLockOutline size="xSmall" />}</IconContainer>
                        <StyledInput
                            name={name}
                            placeholder={placeholder}
                            value={inputValue}
                            onChange={({ target }) => setInputValue(target.value)}
                            onBlur={onBlur}
                            disabled={disabled}
                            autoComplete="off"
                            $outline={$outline}
                        />
                    </InputWrapper>
                    {!disabled && (
                        <DatePicker
                            minDate={minDate}
                            maxDate={maxDate}
                            name={name}
                            preventFlip={preventFlip}
                            disabledDates={disabledDates}
                            $iconColor={$iconColor}
                        />
                    )}
                </HorizontalLayout>
                <InputDescription errorMessage={error?.message} description={description} />
            </FormElement>
        </Wrapper>
    );
};

const IconContainer = styled.div`
    position: absolute;
    top: 50%;
    transform: translateY(-50%);
    right: 9px;
    z-index: 1;
`;

const InputWrapper = styled(HorizontalLayout)`
    position: relative;
`;

const StyledInput = styled(Input)<{ $outline: boolean }>`
    ${({ $outline }) => ($outline ? `border-bottom: 0;` : '')}
`;

const Wrapper = styled.div<{ $outline: boolean }>`
    width: fit-content;
    ${({ $outline, theme }) => ($outline ? `border: 1px solid ${theme.colors.cocoa300}; border-radius: 3px;` : '')}
`;

const DatePicker = ({
    minDate,
    maxDate,
    name,
    preventFlip,
    disabledDates,
    $iconColor,
}: {
    minDate?: Date;
    maxDate?: Date;
    name: string;
    preventFlip: boolean;
    disabledDates?: Date[];
    $iconColor?: string;
}) => {
    const { setValue } = useFormContext();
    const date = useWatch({ name });

    const [open, setOpen] = useState(false);
    const middleware = [offset(8)];
    if (!preventFlip) {
        middleware.push(flip());
    }

    const { refs, strategy, x, y, context } = useFloating({
        placement: 'bottom-end',
        open,
        strategy: 'fixed',
        onOpenChange: setOpen,
        middleware,
        whileElementsMounted: autoUpdate,
    });
    useInteractions([useDismiss(context)]);

    return (
        <>
            <div ref={refs.setReference}>
                <Button
                    size="icon"
                    buttonType="ghost"
                    onClick={() => setOpen(!open)}
                    icon={<StyledIconCalendar $iconColor={$iconColor ?? ''} size="micro" />}
                />
            </div>
            {open && (
                <div
                    ref={refs.setFloating}
                    style={{
                        position: strategy,
                        top: y ?? '',
                        left: x ?? '',
                        zIndex: 1000,
                    }}
                >
                    <StyledCalendar
                        onChange={(newDate) => {
                            setOpen(false);
                            setValue(name, formatDate(newDate, 'SERVER'), {
                                shouldDirty: true,
                                shouldValidate: true,
                            });
                        }}
                        date={date ? parseLocalDate(date) : undefined}
                        minDate={minDate ?? MIN_DATE}
                        maxDate={maxDate ?? MAX_DATE}
                        color={themeDefault.colors.seafoam400}
                        disabledDates={disabledDates}
                    />
                </div>
            )}
        </>
    );
};

const StyledIconCalendar = styled(IconCalendar)<{ $iconColor: string }>`
    ${({ $iconColor }) => ($iconColor ? `color: ${$iconColor};` : '')}
`;

const StyledCalendar = styled(Calendar)`
    box-shadow: 0px 0px 14px #28313c30;
    border-radius: 4px;
    background-color: ${({ theme }) => theme.container.primaryBackground};
    position: relative;

    & .rdrDateDisplayWrapper {
        display: none;
    }

    & .rdrNextPrevButton {
        background: ${({ theme }) => theme.colors.cocoa0};
    }

    & .rdrMonthAndYearPickers select {
        font-weight: 500;
        color: ${({ theme }) => theme.text.primary};
    }

    & .rdrWeekDay {
        font-weight: 500;
        color: ${({ theme }) => theme.text.primary};
    }

    & .rdrMonth {
        padding: 0 0.25rem 0.25rem 0.25rem;
    }

    & .rdrMonthAndYearWrapper {
        height: 40px;
    }

    & .rdrSelected ~ .rdrDayNumber {
        font-weight: 500;
    }
`;

interface DateRangeInputProps {
    title: string;
    startName: string;
    startPlaceholder?: string;
    endName: string;
    endPlaceholder?: string;
    $iconColor?: string;
    $outline?: boolean;
}

export const DateRangeInput = ({
    title,
    startName,
    startPlaceholder = 'Start Date',
    endName,
    endPlaceholder = 'End Date',
    $iconColor,
    $outline,
}: DateRangeInputProps) => {
    return (
        <VerticalLayout $gap={8}>
            {title ? <Title form>{title}</Title> : null}
            <HorizontalLayout $gap={8}>
                <FormDateInput
                    $isVertical
                    label=""
                    placeholder={startPlaceholder}
                    name={startName}
                    $iconColor={$iconColor}
                    $outline={$outline}
                />
                <InputSeparator />
                <FormDateInput
                    $isVertical
                    label=""
                    placeholder={endPlaceholder}
                    name={endName}
                    $iconColor={$iconColor}
                    $outline={$outline}
                />
            </HorizontalLayout>
        </VerticalLayout>
    );
};

const InputSeparator = styled.hr`
    border-top: 1px solid ${({ theme }) => theme.colors.cocoa300};
    margin-top: auto;
    margin-bottom: 15px;
    min-width: 15px;
    height: 1px;
`;
